import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch, connect } from "react-redux";
import { Box } from "@mui/material";
import TestScenarioHeader from "./TestScenarioHeader";
import TestScenarioBody from "./TestScenarioBody";
import LoadingOverlay from "../Common/LoadingOverlay";
import EditDetail from "./EditDetail";
import BuildScenario from "./BuildScenario";
import TestScenarioBuildsDrawer from "../TestScenarios/TestScenarioBuildsDrawer";
import MainLayout from "../../layouts/MainLayout";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { getTestScenarioTags, fetchTestScenarioDetail } from "../../redux-store/testScenarioReducers/testScenarioActions";
import { getOwners } from "../../redux-store/currentUserActions";
import { getAllVariableSets } from "../../redux-store/variableSetReducers/variableSetActions";
import DataFileManager from "./DataFileManager";
import { getProject } from '../../redux-store/currentUserActions';
import { setSelectedProject } from 'reducers/commonSlice.js';

const TestScenarioDetail = function ({ project, testScenarioId, isAiAgent = false, createTestScenarioRequestAIAgent, expandTestCaseIdOnLoad = null }) {
  const dispatch = useDispatch();
  const checkTestScenarioDetail = useSelector((state) => state.testScenario.selectedTestScenarioDetail);
  const isLoading = useSelector((state) => state.testScenario.isLoading);
  const [testScenarioLoading, setTestScenarioLoading] = useState(true);
  const [selectedItemContent, setSelectedItemContent] = useState(null);
  const [error, setError] = useState(null);

  const drawerItems = useMemo(() => [
    { icon: "article", title: "Details", id: "detail" },
    { icon: "play", title: "Run", id: "run" },
    // { icon: "search", title: "Debug", id: "debug", disabled: true },
    { icon: "build", title: "Builds", id: "builds" },
    { icon: "file", title: "Data Files", id: "datafiles" },
  ], []);

  const updateSelectedProjectInState = useCallback(async (projectId) => {
    if(!projectId) return;
    try {
      const response = await dispatch(getProject({ projectId: projectId }));
      await dispatch(setSelectedProject(response.payload));
      localStorage.setItem('lastSelectedProject', JSON.stringify(response.payload));
    } catch (err) {
      setError('Failed to update project');
      console.error('Failed to update project:', err);
    }
  }, []); // dispatch is stable, no need to include it

  const getRequiredInfo = useCallback(async () => {
    setTestScenarioLoading(true);
    try {
      const testScenarioResponse = await dispatch(fetchTestScenarioDetail({ projectId: project.id, testScenarioId }));
      if (testScenarioResponse?.payload === undefined) {
        setError('Failed to fetch test scenario');
        setTestScenarioLoading(false);
        return;
      }
      
      await updateSelectedProjectInState(testScenarioResponse?.payload?.project_id);
      if (testScenarioResponse?.payload?.project_id !== project.id) {
        setTestScenarioLoading(false);
        return;
      }

      await Promise.all([
        dispatch(getTestScenarioTags({ projectId: project.id })),
        dispatch(getOwners({ projectId: project.id })),
        dispatch(
          getAllVariableSets({
            projectId: project.id,
            pageNumber: 1,
          })
        ),
      ]);
    } catch (err) {
      setError('Failed to fetch required information');
      console.error('Failed to fetch required information:', err);
    } finally {
      setTestScenarioLoading(false);
    }
  }, [project.id, testScenarioId]);

  useEffect(() => {
    getRequiredInfo();
  }, [project.id, testScenarioId]); // Changed to directly use the required dependencies

  const renderDrawerItem = useCallback(({ selectedItem, onDrawerClose }) => {
    const props = { projectId: project.id, testScenarioId };
    switch (selectedItem) {
      case "detail":
        setSelectedItemContent(
          <EditDetail {...props} closeDrawer={onDrawerClose} />
        );
        break;
      case "run":
        setSelectedItemContent(
          <BuildScenario {...props} closeDrawer={onDrawerClose} />
        );
        break;
      case "builds":
        setSelectedItemContent(
          <TestScenarioBuildsDrawer {...props} closeDrawer={onDrawerClose} />
        );
        break;
      case "datafiles":
        setSelectedItemContent(
          <DataFileManager {...props} closeDrawer={onDrawerClose} />
        );
        break;
      default:
        setSelectedItemContent(null);
        break;
    }
  }, [project.id, testScenarioId]);

  const memoizedHeaderComponent = useMemo(() => (
    <TestScenarioHeader projectId={project.id} testScenarioId={testScenarioId} />
  ), [project.id, testScenarioId]);

  if (testScenarioLoading) return <LoadingOverlay isLoading={testScenarioLoading} />;
  
  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen w-full">
        <ErrorOutlineIcon style={{ fontSize: 60 }} className="text-red-500 mb-4" />
        <span className="text-2xl">Error</span>
        <span className="text-[var(--text-secondary)]">{error}</span>
      </div>
    );
  }

  if (checkTestScenarioDetail && Object.keys(checkTestScenarioDetail).length === 0 && !testScenarioLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen w-full">
        <ErrorOutlineIcon style={{ fontSize: 60 }} className="text-red-500 mb-4" />
        <span className="text-2xl">Test Scenario does not exist.</span>
        <span className="text-[var(--text-secondary)]">You may have deleted it or it was deleted by another user.</span>
      </div>
    );
  }

  return (
    <MainLayout
      isLoading={isLoading}
      drawerItems={drawerItems}
      renderDrawerItem={renderDrawerItem}
      enableSideDrawer={true}
      contentStyle={{ width: "calc(100% - 87px)", overflow: "auto", maxHeight: "calc(100vh - 145px)", px: 2 }}
      selectedItemContent={selectedItemContent}
      sxLeftSide={{ maxWidth: "100%" }}
      renderHeader={() => memoizedHeaderComponent}
      sxSideDrawer={{ position: 'fixed', top: '109px' }}
    >
      <Box>
        <TestScenarioBody
          project={project}
          testScenarioId={testScenarioId}
          isAiAgent={isAiAgent}
          createTestScenarioRequestAIAgent={createTestScenarioRequestAIAgent}
          expandTestCaseIdOnLoad={expandTestCaseIdOnLoad}
        />
      </Box>
    </MainLayout>
  );
};

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    project: state.common.selectedProject ?? ownProps.project
  }
}

export default connect(mapStateToProps)(TestScenarioDetail);

